import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import {
  useBrandSpecialQualifyingStatus,
  useStoreSpecials,
} from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { Button, Flex, Typography } from '@iheartjane/reefer';
import type { ProductKind } from '@iheartjane/types';
import { ProductKindEnum } from '@iheartjane/types';
import { pluralize } from '@iheartjane/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useMatches, useParams } from 'react-router';

import { useRecommendedSort } from '@jane/dm/internal';
import type {
  AlgoliaProduct,
  ArrayFilterValue,
  JaneSearchState,
  ProductFilterKeys,
  RangeFilterValue,
} from '@jane/search/types';
import {
  AVAILABLE_WEIGHTS,
  buildSearchFilter,
  handleFilterChange,
  handleFilterDeselect,
  updateFacetCounts,
} from '@jane/search/util';
import { useGetStoreSpecials } from '@jane/shared-ecomm/data-access';
import { useGetStore } from '@jane/shared/data-access';
import { useNavigateBack } from '@jane/shared/hooks';
import type { Store, StoreSpecial } from '@jane/shared/types';
import { getApplicablePriceIds } from '@jane/shared/util';

import {
  FilterSection,
  Modal,
  ModalHeader,
  getColumnCount,
} from '../../components';
import type { RootLoaderType } from '../../data-access';
import { getLocalCartUuid, searchOptions } from '../../data-access';
import {
  useKioskSpacing,
  useRowContext,
  useShouldKioskShowGold,
  useTypedRouteLoaderData,
} from '../../hooks';
import {
  useBrandSpecials,
  useFormattedFilterGroups,
  useGoldFacetsAndFilters,
  useProductSort,
  useSearchContext,
} from '../../providers';
import type { OptionalMenuRouteParams } from '../../types';
import {
  getSource,
  isSupportedStoreSpecial,
  trackFilteredProductList,
} from '../../util';

export const Filters = () => {
  const showGold = useShouldKioskShowGold({});

  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const cartUuid = getLocalCartUuid();

  const { category, specialId, brandSpecialId } =
    useParams<OptionalMenuRouteParams>();
  const currentCategoryPage = ProductKindEnum.includes(category as ProductKind)
    ? category
    : null;

  const goBack = useNavigateBack(`stores/${storeId}/menu`);
  const { getKioskSpacing, isIpad } = useKioskSpacing();

  const { resetSearchState, searchState, setSearchState } = useSearchContext();
  const { brandSpecials, brandSpecialFilter } = useBrandSpecials();
  const [initialSearchState] =
    useState<JaneSearchState<AlgoliaProduct>>(searchState);

  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const { data: v1Specials = [] } = useGetStoreSpecials(storeId, {
    enabled: !useV3 && !!storeId,
  });
  const v3SpecialsQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!storeId,
    storeId,
  });
  const specials = useMemo<(StoreSpecial | V3StoreSpecial)[]>(() => {
    if (useV3) {
      return v3SpecialsQuery.data?.pages.flatMap((page) => page.specials) ?? [];
    }
    return v1Specials;
  }, [useV3, v3SpecialsQuery.data, v1Specials]);
  const supportedStoreSpecials = specials.filter((special) =>
    isSupportedStoreSpecial(special)
  );
  const specialInScope = supportedStoreSpecials.find(
    ({ id }) => `${id}` === specialId
  );
  const brandSpecial = brandSpecials.find(
    ({ id }) => `${id}` === brandSpecialId
  );

  const { data: qualifyingStatusData, refetch: refetchQualifyingStatus } =
    useBrandSpecialQualifyingStatus({
      cartUuid: cartUuid ?? undefined,
      specialId: brandSpecial?.id,
      storeId: store?.id,
    });

  const qualifyingStatus = qualifyingStatusData?.qualifying_status;

  const applicablePriceIds = getApplicablePriceIds(
    brandSpecial!,
    qualifyingStatus ?? 'not_qualified'
  );

  // if there are no weight restrictions, we return all of the weights
  const visiblePriceIds =
    applicablePriceIds.length !== AVAILABLE_WEIGHTS.length
      ? applicablePriceIds
      : null;

  const { featuredCategory, featuredFilter, rowId } = useRowContext(store);

  const isGold = category === 'jane-gold';

  const staticSearchOptions = searchOptions({
    categories: [category],
    customRowFilter: isGold ? brandSpecialFilter : featuredFilter,
    storeId,
    storeSpecial: specialInScope,
    brandSpecial,
  });
  const searchFilter = buildSearchFilter({
    searchState,
    staticFilters: staticSearchOptions.staticFilters,
  });
  const storeSearchOptions = searchOptions({
    storeId,
  });
  const { searchSort } = useProductSort({
    defaultSortName: null,
    store,
  });

  const numColumns = getColumnCount();

  /**
   * Step 1: Assembling filter options:
   * Fetch facets from search using only *static* filters.
   * This allows us to draw all possibilities (eg. all product Kinds), and establishes a baseline of whether the
   *  current page can ever allow filtering by such possibilities -- "Should the Vape product Kind always or sometimes
   *  be disabled, on a featured row or special that excludes Vapes?"
   */
  const { searchResultFacets: initialFacets } = useRecommendedSort({
    maxProducts: 0,
    numColumns,
    searchAttributes: ['*'],
    searchFilter: storeSearchOptions.staticFilters,
    searchSort,
    storeId,
  });

  /**
   * Step 2: Apply non-static filters and zip these results with the previously calculated initial facets.
   *
   * Gold facets need to be updated separately due to user segmentation making the
   * count more complicated to calculate.
   */
  const { goldFacets, goldFacetsSearchFilters } = useGoldFacetsAndFilters({
    goldFacetsEnabled:
      showGold && Boolean(initialFacets?.['has_brand_discount']?.['true']),
    staticFilters: staticSearchOptions.staticFilters,
  });

  const goldFilterValues = searchState.filters?.['has_brand_discount'] ?? [];
  const isFilteredByGold = goldFilterValues?.length > 0;

  const {
    searchResultFacets,
    numHits: filteredProductCount,
    isLoading,
  } = useRecommendedSort({
    maxProducts: 0,
    numColumns,
    searchAttributes: ['*'],
    searchFilter: isFilteredByGold ? goldFacetsSearchFilters : searchFilter,
    searchQuery: searchState.searchText ?? '',
    searchSort,
    storeId,
  });

  /**
   * Ensure filtered facets have finished fetching before updating counts.
   * This keeps all filter groups visible between renders.
   */

  const facets = searchResultFacets
    ? updateFacetCounts(
        searchState,
        { ...initialFacets, ...goldFacets },
        searchResultFacets
      )
    : initialFacets;

  const filterGroups = useFormattedFilterGroups(
    facets,
    store,
    {
      canFilterByCategory: !currentCategoryPage,
      canFilterByJaneGold: showGold && brandSpecials?.length > 0,
      canFilterByRating: store.product_reviews_enabled,
      canFilterBySpecial: !featuredCategory && !rowId && !specialInScope,
      canFilterBySubcategoriesOf: currentCategoryPage as ProductKind,
      visiblePriceIds: visiblePriceIds ?? undefined,
    },
    supportedStoreSpecials
  );

  /**
   * Though similar to the one that includes more of searchState, this search is used to inform Mixpanel of the total number of results
   * _before_ the customer begins to filter. This is a general search that applies to multiple menu screens -
   * All products, Category, Featured, Special, Search Results.
   */
  const { numHits: totalProductCount } = useRecommendedSort({
    maxProducts: 0,
    numColumns,
    searchAttributes: ['*'],
    searchFilter: storeSearchOptions.staticFilters,
    searchQuery: searchState.searchText ?? '',
    searchSort,
    storeId,
  });

  const discardSelection = useCallback(() => {
    setSearchState(initialSearchState);
    goBack();
  }, [initialSearchState, goBack, setSearchState]);

  const clearAllFilters = useCallback(
    () => resetSearchState({ searchText: searchState.searchText }),
    [resetSearchState, searchState]
  );

  const onChange = useCallback(
    (
      filterKey: ProductFilterKeys,
      filterValue: ArrayFilterValue | RangeFilterValue
    ) => {
      return setSearchState(
        handleFilterChange(filterKey, filterValue, searchState)
      );
    },
    [searchState, setSearchState]
  );

  const onDeselect = useCallback(
    (filterKey: ProductFilterKeys) => {
      return setSearchState(handleFilterDeselect(filterKey, searchState));
    },
    [searchState, setSearchState]
  );
  const { pathname } = useLocation();
  const matches = useMatches();

  const onShowFilteredProducts = useCallback(() => {
    trackFilteredProductList({
      dealName: specialInScope ? [specialInScope.title] : null,
      filterOptions: searchState,
      filteredProductCount,
      source: getSource({
        currentSpecial: specialInScope,
        isTopper: false,
        matches,
        pathname,
      }),
      totalProductCount: totalProductCount ?? store.product_count,
    });
  }, [
    filteredProductCount,
    totalProductCount,
    matches,
    pathname,
    searchState,
    specialInScope,
    store.product_count,
    specialInScope,
  ]);

  useEffect(() => {
    if (brandSpecialId && brandSpecial && cartUuid) {
      refetchQualifyingStatus();
    }
  }, []);

  return (
    <Modal
      appId="root"
      onRequestClose={discardSelection}
      open
      overflow="hidden"
      variant="standard"
      overlayClose
    >
      <ModalHeader onRequestClose={discardSelection}>
        <Flex alignItems="center" grow={1} justifyContent="space-between">
          <Typography mr={96} variant={isIpad ? 'header-bold' : 'title-bold'}>
            All filters
          </Typography>
          <Flex>
            <Button
              label={<Typography variant="body-bold">Clear all</Typography>}
              onClick={clearAllFilters}
              size={isIpad ? 'small' : 'default'}
              variant="tertiary"
            />
            <Button
              disabled={filteredProductCount === 0}
              loading={isLoading}
              ml={getKioskSpacing(24, 16)}
              label={
                <Typography color="text-info" variant="body-bold">
                  Show{' '}
                  {pluralize({
                    noun: 'product',
                    number: filteredProductCount,
                  })}
                </Typography>
              }
              size={isIpad ? 'small' : 'default'}
              onClick={onShowFilteredProducts}
              to={pathname.replace(/\/filters$/, '')}
            />
          </Flex>
        </Flex>
      </ModalHeader>
      <Modal.Content padding={false}>
        {filterGroups.map((group) => (
          <FilterSection
            key={`${group.key}-${group.label}`}
            filterGroup={group}
            onChange={onChange}
            onDeselect={onDeselect}
            searchState={searchState}
          />
        ))}
      </Modal.Content>
    </Modal>
  );
};
